import { type PowerSyncDrizzleDB, user } from "@gymlog/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── §4.11: `user` is NOT excluded from verified writes ───────────────────────
 *
 * The guide (`infra/guides/POWERSYNC_VERIFIED_WRITE.md` §4.1) warns that
 * excluding `user` "because the server already created the row" was proven
 * FALSE in all 11 audited repos, including this one.
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) seeded
 * the LOCAL row with
 * `db.insert(user).values({ id: uid, … })` when a prior
 * `SELECT` missed it.
 * Until 2026-08-13 it did so for EVERY `useSession()` `uid`, without
 * distinguishing a guest from an account or waiting for
 * `status().hasSynced` before deciding the row was absent
 * (it only checked `if (!db || !uid) return`).
 * That was the window's source: the local INSERT raced the server's
 * unconditional `INSERT INTO "user"` for every signup/OAuth
 * (`packages/core-rust/src/db/user.rs:101,350`). The effect is now guarded by
 * `uid !== VISITOR_ID` (see `theme-provider.tsx` and
 * `theme-provider-seed-window.test.ts`). The phantom INSERT no longer
 * fires when `uid` is `VISITOR_ID`.
 * Real accounts are unaffected.
 *
 * That does NOT retire `patchRowVerified`. The guard closes the race between
 * TWO inserts (local vs. server), not sync delay itself: a new account's
 * `user` row exists on the SERVER but may not yet have reached the client
 * through a checkpoint.
 * In that window, if the user changes language/font/theme/avatar/
 * blur/follow-device, a raw `UPDATE` would still match zero rows and
 * silently lose the change.
 * The guard removed one competing insert.
 * It did not remove the sync-delay window.
 * Therefore all six mutations below
 * use `patchRowVerified`.
 *
 * The same checkpoint risk covers `exercise`
 * (`packages/shared/src/gym/mutations.ts`, with
 * `scope: [{ column: exercise.user_id, value: userId }]` to protect the
 * read-only catalog) and two transactions that call `waitForRowPresent` first
 * (`restoreRoutineVersionMutation`, `swapRoutineExercisesMutation`).
 * None uses `ThemeProvider`'s startup guard,
 * which only seeds local `user`.
 *
 * Covered by `packages/shared/src/users/__tests__/mutations-verified-write.test.ts`.
 *
 * ── Why ALL mutations below use `.where(eq(user.id, userId))` ──────────────
 *
 * Four (`locale`, `global_theme_id`, `is_blur`, `follow_device_theme`)
 * previously used:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * Guest UPDATE ran **without `WHERE`**, rewriting EVERY local `user` row.
 * The branch mirrored `getUserThemeSettingsQuery`,
 * whose guest read uses `.limit(1)` without a filter.
 * But a read `limit(1)` may pick the wrong row;
 * an unfiltered UPDATE destroys them all.
 *
 * The "one guest `user` row" premise fails during guest→real upgrade: the
 * guest DB CONNECTS to the real connector and subscribes to every stream.
 * `organizations` syncs one `user` row per co-member, leaving 1 + 1 + N rows.
 * `user` is not `localOnly`; each touched row enqueues its own `ps_crud` op.
 *
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) seeds the guest
 * with `db.insert(user).values({ id: uid, … })`; guest `uid` is `VISITOR_ID`.
 * So `eq(user.id, userId)` targets the SAME row the old branch meant:
 * identical for one row, bounded otherwise.
 * `updateUserFontFamilyMutation` and
 * `updateUserImageMutation` already used it without a guest branch.
 *
 * Covered by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 * `patchRowVerified` keeps `.where(eq(user.id, userId))`; verification never
 * restores the unfiltered branch.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * Like language, font, and theme, it is local-first: the new photo appears
 * immediately and survives an offline cold reload. The BINARY uses REST
 * (`POST /api/avatar`) because it cannot fit the sync queue; only the uploaded
 * key comes through here.
 *
 * `null` = "remove the photo" (initials return).
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
